"use client";

/**
 * AdminSteps — 단계형 진행 표시기 (Step / Wizard)
 *
 * 여러 단계로 나뉜 등록/설정 폼(마법사)에서 현재 진행 단계를 보여준다.
 * current(0부터 시작하는 인덱스)보다 앞선 단계는 완료(체크) 표시로, 현재 단계는 primary 색으로
 * 강조되고, 뒤의 단계는 흐리게 표시된다. onChange 를 주면 이미 지난(완료된) 단계를 클릭해서
 * 되돌아갈 수 있다 — 아직 오지 않은 단계는 검증을 건너뛰지 못하도록 클릭이 막혀 있다.
 *
 * 모바일(좁은 화면)에서는 원형 스텝을 모두 나열하는 대신 "현재 단계 제목 + N/전체 + 진행 바"로
 * 축약해서 보여준다 (direction="horizontal" 일 때만 — vertical 은 이미 좁은 화면에 적합하다).
 *
 * @example
 * <AdminSteps
 *   current={step}
 *   onChange={setStep}
 *   items={[
 *     { title: "기본 정보" },
 *     { title: "도어 & 카메라" },
 *     { title: "결제 / 인증" },
 *     { title: "외부 시스템" },
 *     { title: "확인" },
 *   ]}
 * />
 */
import { Check } from "lucide-react";
import type { ReactNode } from "react";

import { useIsMobile } from "../../hooks/use-is-mobile";
import { cn } from "../../lib/utils";

export type AdminStepStatus = "process" | "error";
export type AdminStepsDirection = "horizontal" | "vertical";
export type AdminStepsSize = "sm" | "md";

export interface AdminStepItem {
  title: ReactNode;
  /** 제목 아래 보조 설명 */
  description?: ReactNode;
  /** 완료된 단계라도 클릭 이동을 막는다 */
  disabled?: boolean;
}

export interface AdminStepsProps {
  items: AdminStepItem[];
  /** 현재 단계 인덱스 (0부터 시작) */
  current: number;
  /** 현재 단계의 상태. "error" 를 주면 현재 단계가 destructive 색으로 표시된다. (기본 "process") */
  status?: AdminStepStatus;
  /** 완료된 단계를 클릭해 되돌아갈 수 있게 한다. 안 주면 클릭 이동 없이 표시만 한다. */
  onChange?: (index: number) => void;
  /** 배치 방향 (기본 "horizontal") */
  direction?: AdminStepsDirection;
  /** 크기 (기본 "md") */
  size?: AdminStepsSize;
  className?: string;
}

const CIRCLE_SIZE: Record<AdminStepsSize, string> = {
  sm: "size-6 text-xs",
  md: "size-7 text-sm",
};

const ICON_SIZE: Record<AdminStepsSize, string> = {
  sm: "size-3",
  md: "size-3.5",
};

const TITLE_SIZE: Record<AdminStepsSize, string> = {
  sm: "text-xs",
  md: "text-sm",
};

export function AdminSteps({
  items,
  current,
  status = "process",
  onChange,
  direction = "horizontal",
  size = "md",
  className,
}: AdminStepsProps) {
  const isMobile = useIsMobile();

  if (isMobile && direction === "horizontal") {
    return (
      <MobileSteps
        items={items}
        current={current}
        status={status}
        className={className}
      />
    );
  }

  const isVertical = direction === "vertical";

  return (
    <ol
      className={cn(
        "flex w-full",
        isVertical ? "flex-col" : "items-center",
        className
      )}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isDone = index < current;
        const isCurrent = index === current;
        const isError = isCurrent && status === "error";
        const canClick = !!onChange && !item.disabled && isDone;
        const connectorFilled = index < current;

        const circle = (
          <span
            className={cn(
              "flex shrink-0 items-center justify-center rounded-full border font-medium",
              CIRCLE_SIZE[size],
              isError
                ? "border-destructive bg-destructive text-destructive-foreground"
                : isDone
                ? "border-primary bg-primary text-primary-foreground"
                : isCurrent
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background text-muted-foreground"
            )}
          >
            {isDone ? <Check className={ICON_SIZE[size]} /> : index + 1}
          </span>
        );

        const label = (
          <span className="flex min-w-0 flex-col text-left">
            <span
              className={cn(
                TITLE_SIZE[size],
                "truncate",
                isError
                  ? "font-semibold text-destructive"
                  : isCurrent
                  ? "font-semibold text-foreground"
                  : isDone
                  ? "font-medium text-foreground"
                  : "font-medium text-muted-foreground"
              )}
            >
              {item.title}
            </span>
            {item.description && (
              <span className="truncate text-xs text-muted-foreground">
                {item.description}
              </span>
            )}
          </span>
        );

        const content = (
          <button
            type="button"
            disabled={!canClick}
            aria-current={isCurrent ? "step" : undefined}
            onClick={canClick ? () => onChange?.(index) : undefined}
            className={cn(
              "flex min-w-0 items-center gap-2 rounded-md bg-transparent p-0 text-left",
              canClick ? "cursor-pointer hover:opacity-80" : "cursor-default"
            )}
          >
            {circle}
            {label}
          </button>
        );

        if (isVertical) {
          return (
            <li key={index} className={cn("flex gap-3", !isLast && "pb-6")}>
              <div className="flex flex-col items-center">
                {circle}
                {!isLast && (
                  <span
                    className={cn(
                      "mt-1 w-px flex-1",
                      connectorFilled ? "bg-primary" : "bg-border"
                    )}
                  />
                )}
              </div>
              <button
                type="button"
                disabled={!canClick}
                aria-current={isCurrent ? "step" : undefined}
                onClick={canClick ? () => onChange?.(index) : undefined}
                className={cn(
                  "flex-1 bg-transparent p-0 pt-0.5 text-left",
                  canClick ? "cursor-pointer hover:opacity-80" : "cursor-default"
                )}
              >
                {label}
              </button>
            </li>
          );
        }

        return (
          <li key={index} className="flex min-w-0 flex-1 items-center">
            {content}
            {!isLast && (
              <span
                className={cn(
                  "mx-3 h-px flex-1",
                  connectorFilled ? "bg-primary" : "bg-border"
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function MobileSteps({
  items,
  current,
  status,
  className,
}: {
  items: AdminStepItem[];
  current: number;
  status: AdminStepStatus;
  className?: string;
}) {
  const total = items.length;
  const activeItem = items[current];
  const isError = status === "error";
  const percent = total > 0 ? ((current + 1) / total) * 100 : 0;

  return (
    <div className={cn("w-full", className)}>
      <div className="flex items-center justify-between gap-3">
        <span
          className={cn(
            "text-sm font-semibold",
            isError ? "text-destructive" : "text-foreground"
          )}
        >
          {current + 1}. {activeItem?.title}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {current + 1} / {total}
        </span>
      </div>
      {activeItem?.description && (
        <p className="mt-0.5 text-xs text-muted-foreground">
          {activeItem.description}
        </p>
      )}
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            isError ? "bg-destructive" : "bg-primary"
          )}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
